import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    TextField, Select, MenuItem, FormControl, InputLabel, Grid, Typography,
    FormHelperText, Button, Box, Alert,
} from '@mui/material';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';

/**
 * O bloco de cadastro do representante, compartilhado pelas telas de criar e
 * de editar operador.
 *
 * Spec: `docs/superpowers/specs/2026-10-05-representantes-design.md` (na api).
 *
 * ## Dois campos, porque são dois fatos
 *
 * A primeira versão deste bloco tinha UM campo ("Carteira", o nome do
 * representante) e estava errada. O dono do produto estranhou em 06/10 -- "a
 * empresa do representante é criada como? deveria ser ao colocar o registro e
 * cnpj" -- e a medição em produção deu razão a ele, por dois motivos
 * independentes:
 *
 * **1. O nome não identifica o representante.** O código 16 aparece com duas
 * grafias: "VIRTUAL OBJECT - GILMER (ATENDIMENTO SYSFAR)" com 6 clientes e
 * "VIRTUAL OBJECT - GILMER" com 1. Pelo nome, o Gilmer teria duas carteiras:
 * entraria, veria 6 clientes, e o sétimo não existiria para ele.
 *
 * **2. O cadastro do representante não revela o código dele.** O Virtual
 * Object está cadastrado no grupo 53 ("REPRESENTANTES") enquanto os clientes
 * dele carregam o grupo 16. Em dois casos de três o cadastro coincide com o
 * código; no terceiro, não. Deduzir um do outro erraria em silêncio.
 *
 * Por isso:
 *
 * - **Representante** (obrigatório para cercar) -- escolhido numa lista, uma
 *   entrada por CÓDIGO. É o que decide quais clientes ele vê.
 * - **Registro** (opcional) -- busca a empresa dele no ERP e traz razão
 *   social, CNPJ e cidade. É o cadastro, e não manda na visibilidade.
 *
 * Opcional de verdade: medido em produção, o TECHMASTER é o grupo 54, tem 2
 * clientes e **não tem empresa cadastrada**. Exigir o registro deixaria esse
 * representante impossível de configurar.
 */

/**
 * Os representantes conhecidos, buscados UMA vez por sessão.
 *
 * A lista mora na api, agrupada por código. Falha não fica em cache -- um erro
 * de rede momentâneo não pode deixar o seletor vazio pelo resto da sessão,
 * porque seletor vazio aqui parece "não há representantes cadastrados".
 */
let promessaDosRepresentantes = null;

export const useRepresentantesConhecidos = () => {
    const [representantes, setRepresentantes] = useState([]);
    const [falhou, setFalhou] = useState(false);

    useEffect(() => {
        let cancelado = false;

        if (!promessaDosRepresentantes) {
            promessaDosRepresentantes = http.get('/companies/representantes')
                .then(({ data }) => (Array.isArray(data) ? data : []))
                .catch(() => { promessaDosRepresentantes = null; return null; });
        }

        promessaDosRepresentantes.then((lista) => {
            if (cancelado) return;
            if (lista === null) {
                setFalhou(true);
                setRepresentantes([]);
                return;
            }
            setFalhou(false);
            setRepresentantes(lista);
        });

        return () => { cancelado = true; };
    }, []);

    return { representantes, falhou };
};

/** Só para teste: zera o cache de módulo entre casos. */
export const _limparCacheDasCarteiras = () => { promessaDosRepresentantes = null; };

export const SEM_REPRESENTANTE = '';

const CadastroDoRepresentante = ({ valores, aoMudar, operadores = [], idDoProprio = null }) => {
    // `ThemeContext` é criado sem valor padrão, então fora do provider ele vem
    // `undefined`. Sem as interrogações o componente quebra no render, que é
    // onde o erro não diz qual é o problema.
    const { theme } = useContext(ThemeContext) || {};
    const { representantes, falhou } = useRepresentantesConhecidos();

    const [registro, setRegistro] = useState('');
    const [buscando, setBuscando] = useState(false);
    const [erroDaBusca, setErroDaBusca] = useState('');

    /**
     * Os dados das empresas já buscadas, por id.
     *
     * A empresa EXIBIDA é derivada de `valores.representante_company_id` --
     * nunca de um estado próprio. Guardar a empresa escolhida em estado local
     * cria duas fontes de verdade que brigam: o efeito que carrega pelo id
     * apagava o resultado da busca enquanto o formulário pai ainda não tinha
     * ecoado o novo valor, e a empresa recém-encontrada piscava e sumia.
     */
    const [empresasPorId, setEmpresasPorId] = useState({});

    const corPrimaria = theme?.palette?.text?.primary;
    const corSecundaria = theme?.palette?.text?.secondary;

    const companyId = valores.representante_company_id || null;

    const empresa = companyId ? (empresasPorId[companyId] || null) : null;

    const guardarEmpresa = useCallback((dados) => {
        if (dados && dados.id) {
            setEmpresasPorId((atual) => ({ ...atual, [dados.id]: dados }));
        }
    }, []);

    // Editando um representante que já tem empresa: mostra qual é, em vez de
    // um campo vazio que faria o admin achar que o vínculo se perdeu.
    useEffect(() => {
        let cancelado = false;

        if (!companyId || empresasPorId[companyId]) return undefined;

        http.get(`/companies/${companyId}`)
            .then(({ data }) => { if (!cancelado) guardarEmpresa(data); })
            .catch(() => {});

        return () => { cancelado = true; };
    }, [companyId, empresasPorId, guardarEmpresa]);

    const buscarPorRegistro = useCallback(async () => {
        const termo = registro.trim();
        if (termo === '') return;

        setBuscando(true);
        setErroDaBusca('');
        try {
            const { data } = await http.get('/companies/por-registro', { params: { registro: termo } });
            guardarEmpresa(data);
            aoMudar('representante_company_id', data.id);
            setRegistro('');
        } catch (e) {
            setErroDaBusca(e?.response?.data?.message || 'Não foi possível buscar esse registro.');
        } finally {
            setBuscando(false);
        }
    }, [registro, aoMudar, guardarEmpresa]);

    const codigoAtual = valores.representante_codigo || SEM_REPRESENTANTE;

    // O código gravado pode não estar mais na lista -- o último cliente
    // daquele representante pode ter mudado de grupo no ERP. Sem isto o
    // seletor abriria em branco e o próximo salvamento tiraria o acesso do
    // representante sem ninguém tocar no campo.
    const conhecidos = representantes.map((r) => String(r.codigo));
    const opcoes = codigoAtual !== SEM_REPRESENTANTE && !conhecidos.includes(String(codigoAtual))
        ? [{ codigo: codigoAtual, nome: `Código ${codigoAtual} (sem clientes hoje)`, clientes: 0 }, ...representantes]
        : representantes;

    // O cadastro do representante pode estar classificado em outro grupo --
    // é o caso real do Virtual Object (cadastro no 53, clientes no 16). Avisar
    // é honesto; corrigir sozinho seria decidir a carteira por conta própria.
    const grupoDivergente = empresa
        && empresa.service_group
        && codigoAtual !== SEM_REPRESENTANTE
        && String(empresa.service_group) !== String(codigoAtual);

    const limparEmpresa = () => {
        setErroDaBusca('');
        aoMudar('representante_company_id', null);
    };

    return (
        <>
            <Typography
                variant="subtitle2"
                sx={{ marginTop: 3, marginBottom: 1, color: corPrimaria }}
            >
                Representante
            </Typography>

            <FormControl fullWidth margin="normal">
                <InputLabel>Representante</InputLabel>
                <Select
                    value={codigoAtual}
                    onChange={({ target }) => aoMudar('representante_codigo', target.value)}
                    label="Representante"
                    variant="outlined"
                    inputProps={{ 'data-testid': 'carteira-do-representante' }}
                >
                    {/* "Nenhum" é opção de verdade, e não placeholder: é como o
                        admin DESLIGA a cerca de quem deixou de ser
                        representante. Sem ela, a única saída seria apagar o
                        operador. */}
                    <MenuItem value={SEM_REPRESENTANTE}>Nenhum (vê todos os clientes)</MenuItem>
                    {opcoes.map((r) => (
                        <MenuItem key={r.codigo} value={String(r.codigo)}>
                            {r.nome} {r.clientes ? `— ${r.clientes} cliente${r.clientes > 1 ? 's' : ''}` : ''}
                        </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                    {falhou
                        ? 'Não foi possível carregar os representantes. Recarregue a página antes de salvar.'
                        : 'Define quais clientes, chamados e conversas este operador enxerga.'}
                </FormHelperText>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 2 }}>
                <TextField
                    label="Registro do representante (opcional)"
                    value={registro}
                    onChange={({ target }) => setRegistro(target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); buscarPorRegistro(); } }}
                    variant="outlined"
                    fullWidth
                    helperText="Busca a empresa no ERP e traz razão social, CNPJ e cidade."
                    inputProps={{ 'data-testid': 'registro-do-representante' }}
                />
                <Button
                    variant="outlined"
                    onClick={buscarPorRegistro}
                    disabled={buscando || registro.trim() === ''}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                    data-testid="buscar-registro"
                >
                    {buscando ? 'Buscando...' : 'Buscar'}
                </Button>
            </Box>

            {erroDaBusca !== '' && (
                <Alert severity="warning" sx={{ mt: 1 }}>{erroDaBusca}</Alert>
            )}

            {empresa && (
                <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ color: corPrimaria }} data-testid="empresa-do-representante">
                        {empresa.business_name}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: corSecundaria }}>
                        Registro {empresa.code || '—'} · CNPJ {empresa.cnpj || '—'} · {empresa.city || '—'}
                        {empresa.state ? `/${empresa.state}` : ''}
                    </Typography>
                    <Button size="small" onClick={limparEmpresa} sx={{ mt: 0.5, pl: 0 }}>
                        Remover vínculo
                    </Button>
                </Box>
            )}

            {grupoDivergente && (
                <Alert severity="info" sx={{ mt: 1 }} data-testid="aviso-de-grupo-divergente">
                    O cadastro desta empresa está no grupo {empresa.service_group}
                    {empresa.service_group_description ? ` (${empresa.service_group_description})` : ''},
                    diferente do representante escolhido. Isso é comum e não é erro — quem define
                    os clientes é o representante escolhido acima.
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Responsável</InputLabel>
                        <Select
                            value={valores.responsavel_id || ''}
                            onChange={({ target }) => aoMudar('responsavel_id', target.value || null)}
                            label="Responsável"
                            variant="outlined"
                            inputProps={{ 'data-testid': 'responsavel-do-representante' }}
                        >
                            <MenuItem value="">Nenhum</MenuItem>
                            {(operadores || [])
                                .filter((o) => o && o.id && Number(o.id) !== Number(idDoProprio))
                                .map((o) => (
                                    <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
                                ))}
                        </Select>
                        <FormHelperText>
                            Funcionário de um representante. Sem representante próprio, enxerga o do responsável.
                        </FormHelperText>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth margin="normal">
                        <TextField
                            label="Telefone"
                            value={valores.telefone || ''}
                            onChange={({ target }) => aoMudar('telefone', target.value)}
                            variant="outlined"
                            inputProps={{
                                style: { color: corSecundaria },
                                'data-testid': 'telefone-do-representante',
                            }}
                        />
                    </FormControl>
                </Grid>
            </Grid>
        </>
    );
};

export default CadastroDoRepresentante;
